'use client'

import { useAuthorization } from '@entities/authentication'
import { userEntityQueries } from '@entities/users'
import { CurriculumType, LessonEntity, LessonTicketEntity } from '@server/schemas'
import { useI18n } from '@shared/config/i18n'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react'

import { lessonEntityQueries } from '../../api'
import { LessonBookingPolicy } from '../policies/booking'
import { LessonViewModel } from './card'

/**
 * LessonViewModel Context
 * ViewModel 인스턴스를 하위 컴포넌트에 제공
 */
export const LessonViewModelContext = createContext<
  Nullable<{
    viewModel: LessonViewModel
    curriculumType: CurriculumType
    updateViewModel: (lessonEntity: LessonEntity, lessonTicketEntities: LessonTicketEntity[]) => LessonViewModel
  }>
>(null)

/**
 * LessonViewModel Provider
 *
 * entities 계층에서 제공:
 * - ViewModel 생성 및 관리
 * - 필요한 엔티티 자동 fetch (user, tickets)
 * - updateViewModel 함수 제공
 */
export const LessonViewModelProvider = ({
  lessonEntity,
  curriculumType,
  children,
}: PropsWithChildren<{ lessonEntity: LessonEntity; curriculumType: CurriculumType }>) => {
  const { bearerToken } = useAuthorization()
  const { effectiveLocale } = useI18n()
  const { data: userEntity } = useSuspenseQuery({ ...userEntityQueries.getCurrentUser({ bearerToken }) })
  const { data: lessonTicketEntities } = useSuspenseQuery({
    ...lessonEntityQueries.tickets({ bearerToken }),
  })

  // ViewModel 생성
  const createViewModel = useCallback(
    (lesson: LessonEntity, tickets: LessonTicketEntity[]) => {
      const bookingPolicy = new LessonBookingPolicy(lesson, userEntity, tickets, curriculumType)
      return new LessonViewModel(lesson, bookingPolicy, userEntity.timezoneCode, effectiveLocale)
    },
    [curriculumType, effectiveLocale, userEntity],
  )

  const [viewModel, setViewModel] = useState(() => createViewModel(lessonEntity, lessonTicketEntities))

  useEffect(() => {
    setViewModel(createViewModel(lessonEntity, lessonTicketEntities))
  }, [createViewModel, lessonEntity, lessonTicketEntities])

  const updateViewModel = (lessonEntity: LessonEntity, lessonTicketEntities: LessonTicketEntity[]) => {
    const newViewModel = createViewModel(lessonEntity, lessonTicketEntities)
    setViewModel(newViewModel)
    return newViewModel
  }

  return (
    <LessonViewModelContext.Provider value={{ viewModel, curriculumType, updateViewModel }}>
      {children}
    </LessonViewModelContext.Provider>
  )
}
